import { Suspense, lazy, useEffect } from "react";

import { BrowserRouter, Switch } from "react-router-dom";

import { BaseLoader } from "components";

import { RoutePaths } from "./RoutePaths";
import {
  HomeRoute,
  AuthRoute,
  VerifyEmailRoute,
  ExpertScheduleRoute,
  PageNotFoundRoute,
  ExpertHomeRoute,
  ExpertProfileRoute,
  ExpertsCountryRoute,
  ExpertsRoute,
  SettingsRoute,
  EditProfileRoute,
  SelectedExpertProfileRoute,
  StudentLessonsRoute,
  IndividualLessonsRoute,
  GroupLessonsRoute,
  LessonRoute,
  BankDetailsRoute,
  StudentSelectReservationTimeSlotRoute,
  StudentSelectTimeSlotRoute,
  ExpertRevenueManagementRoute,
  ExpertViewEnrollmentsRoute,
  ExpertEnrollmentsLessonReportRoute,
  StudentPurchasePointsRoute,
  PasswordResetRoute,
  ServiceAgreementRoute,
  UserGuideRoute,
  ListOfReservationRoute,
  StaticPageRoute,
  TeacherDetailsRoute,
  ContactFormRoute,
  ContactFormSuccessRoute
} from "./RoutingList";
import { useAuthSelectors } from "redux/selectors";
import useAppSelectors from "../redux/selectors/appSelectors";
import { useTranslation } from "react-i18next";

const NotFound = lazy(() => import("view/notFound"));
const Home = lazy(() => import("view/home"));
const ExpertHome = lazy(() => import("view/expertHome"));
const ExpertProfile = lazy(() => import("view/expertProfile"));
const Auth = lazy(() => import("view/auth"));
const VerifyEmail = lazy(() => import("view/auth/verifyEmail"));
const Lesson = lazy(() => import("view/lessons/lesson"));
const ExpertScheduleSchedule = lazy(
  () => import("view/expertSchedule/expertScheduleShedule")
);
const ExpertScheduleTemplate = lazy(
  () => import("view/expertSchedule/expertSheduleTemplate")
);
const ExpertScheduleReservationList = lazy(
  () => import("view/expertSchedule/expertScheduleReservationList")
);
const ExpertsCountry = lazy(() => import("view/expertsCountry"));
const BankDetails = lazy(() => import("view/bankDetails"));
const AllExpertsList = lazy(() => import("view/expertsList/all"));
const FavouriteExpertsList = lazy(() => import("view/expertsList/favourite"));
const SelectedExpertProfile = lazy(() => import("view/expertsList/profile"));
const Settings = lazy(() => import("view/settings"));
const StudentLessons = lazy(() => import("view/studentLessons"));
const IndividualLessonsCompleted = lazy(
  () => import("view/studentLessons/individualLessonsCompleted")
);
const IndividualLessonsReserved = lazy(
  () => import("view/studentLessons/individualLessonsReserved")
);
const IndividualLessonsNotUsed = lazy(
  () => import("view/studentLessons/individualLessonsNotUsed")
);
const GroupLessons = lazy(() => import("view/studentLessons/groupLessons"));
const ExpertReservationsIndividual = lazy(
  () => import("view/expertListofReservations/IndividualReservations")
);
const ExpertReservationsGroup = lazy(
  () => import("view/expertListofReservations/GroupReservations")
);
const EditProfile = lazy(() => import("view/editProfile"));
const Reservation = lazy(() => import("view/reservation"));
const StudentSelectReservationTimeSlot = lazy(
  () => import("view/studentSelectReservationTimeSlot")
);
const StudentSelectTimeSlot = lazy(() => import("view/studentSelectTimeslot"));
const ExpertRevenueManagement = lazy(
  () => import("view/expertRevenueManagement")
);
const ExpertViewEnrollments = lazy(() => import("view/expertViewEnrollments"));
const ExpertEnrollmentsLessonReport = lazy(
  () => import("view/expertEnrollmentsLessonReport")
);
const StudentPurchasePoints = lazy(() => import("view/studentPurchasePoints"));
const PasswordReset = lazy(() => import("view/auth/PasswordReset"));
const ServiceAgreement = lazy(() => import("view/serviceAgreement"));
const UserGuide = lazy(() => import("view/userGuide"));
const LessonPreview = lazy(() => import("view/lessons/preivew"));
const Lessons = lazy(() => import("view/lessons/lessons"));
const CreateSchedule = lazy(() => import("view/lessons/createSchedule"));
const ReservedLessons = lazy(() => import("view/lessons/reservedLessons"));

const TeacherDetails = lazy(() => import("view/TeacherDetails"));

// static pages
const CancellationPolicy = lazy(
  () => import("view/StaticPages/CancellationPolicy")
);
const HowToUseGuide = lazy(() => import("view/StaticPages/HowToUseGuide"));
const InstructorPolicy = lazy(() => import("view/StaticPages/InstructorPolicy"));
const ExpertRegisterInfo = lazy(
  () => import("view/StaticPages/ExpertRegisterationInfo")
);
const PaymentPolicy = lazy(() => import("view/StaticPages/PaymentPolicy"));
const TermsOfService = lazy(() => import("view/StaticPages/TermsOfService"));
const TransactionLaw = lazy(() => import("view/StaticPages/TransactionLaw"));
const privacyPolicy = lazy(() => import("view/StaticPages/PrivacyPolicy"));
const ExpertRegisterationInfo = lazy(
  () => import("view/StaticPages/ExpertRegisterationInfo")
);
const ExpertRegisterationSuccess = lazy(
  () => import("view/StaticPages/ExpertRegisterationSuccess")
);
const ExpertRegisterationConfirm = lazy(
  () => import("view/StaticPages/ExpertRegistrationConfirm")
);
const ExpertView = lazy(
  () => import("view/expertView")
);
const ContactForm = lazy(
  () => import("view/contactForm")
);
const ContactFormSuccess = lazy(
  () => import("view/contactFormSuccess")
);

const Router = () => {
  const { selectAuthUser } = useAuthSelectors();
  const { language } = useAppSelectors();
  const { i18n } = useTranslation();

  useEffect(() => {
    i18n.changeLanguage(language);
  }, []);

  return (
    <BrowserRouter>
      <Suspense fallback={<BaseLoader />}>
        <Switch>
          <HomeRoute exact path={RoutePaths.root} component={Home} />
          <ExpertHomeRoute
            exact
            path={RoutePaths.expertHome}
            component={ExpertHome}
          />
          <ExpertProfileRoute
            exact
            path={RoutePaths.expertProfile}
            component={ExpertProfile}
          />
           <ExpertProfileRoute
            exact
            path={RoutePaths.expertView}
            component={ExpertView}
          />
          <ExpertProfileRoute
            exact
            path={RoutePaths.expertRegisterInfo}
            component={ExpertRegisterationInfo}
          />
          <ExpertProfileRoute
            exact
            path={RoutePaths.expertRegisterSuccess}
            component={ExpertRegisterationSuccess}
          />
          <LessonRoute exact path={RoutePaths.newLesson} component={Lesson} />
          <LessonRoute exact path={RoutePaths.editLesson} component={Lesson} />
          <LessonRoute exact path={RoutePaths.lessons} component={Lessons} />
          <LessonRoute exact path={RoutePaths.createSchedule} component={CreateSchedule} />
          <LessonRoute exact path={RoutePaths.reservedLessons} component={ReservedLessons} />
          <LessonRoute
            exact
            path={RoutePaths.lessonPreview}
            component={LessonPreview}
          />
          <AuthRoute
            exact
            path={RoutePaths.login}
            component={Auth}
            authUser={selectAuthUser}
          />
          <VerifyEmailRoute
            exact
            path={RoutePaths.verifyEmail}
            component={VerifyEmail}
          />
          <ExpertScheduleRoute
            exact
            path={RoutePaths.expertScheduleSchedule}
            component={ExpertScheduleSchedule}
          />
          <ExpertScheduleRoute
            exact
            path={RoutePaths.expertScheduleTemplate}
            component={ExpertScheduleTemplate}
          />
          <ExpertScheduleRoute
            exact
            path={RoutePaths.expertScheduleReservationList}
            component={ExpertScheduleReservationList}
          />
          <ExpertsCountryRoute
            exact
            path={RoutePaths.expertsCountry}
            component={ExpertsCountry}
          />
          <BankDetailsRoute
            exact
            path={RoutePaths.bankDetails}
            component={BankDetails}
          />
          <ExpertsRoute
            exact
            path={RoutePaths.allExperts}
            component={AllExpertsList}
          />
          <ExpertsRoute
            exact
            path={RoutePaths.favouriteExperts}
            component={FavouriteExpertsList}
          />
          <ListOfReservationRoute
            exact
            path={RoutePaths.lessonsIndividual}
            component={ExpertReservationsIndividual}
          />
          <ListOfReservationRoute
            exact
            path={RoutePaths.lessonsGroup}
            component={ExpertReservationsGroup}
          />
          <SettingsRoute
            exact
            path={RoutePaths.settings}
            component={Settings}
          />
          <EditProfileRoute
            exact
            path={RoutePaths.editProfile}
            component={EditProfile}
          />
          <SelectedExpertProfileRoute
            exact
            path={RoutePaths.selectedExpert}
            component={SelectedExpertProfile}
          />
          <SelectedExpertProfileRoute
            exact
            path={RoutePaths.reservation}
            component={Reservation}
          />
          <StudentLessonsRoute
            exact
            path={RoutePaths.studentLessons}
            component={StudentLessons}
          />
          <IndividualLessonsRoute
            exact
            path={RoutePaths.individualLessonsNotUsed}
            component={IndividualLessonsNotUsed}
          />
          <IndividualLessonsRoute
            exact
            path={RoutePaths.individualLessonsCompleted}
            component={IndividualLessonsCompleted}
          />
          <IndividualLessonsRoute
            exact
            path={RoutePaths.individualLessonsReserved}
            component={IndividualLessonsReserved}
          />
          <GroupLessonsRoute
            exact
            path={RoutePaths.groupLessons}
            component={GroupLessons}
          />
          <StudentSelectReservationTimeSlotRoute
            exact
            path={RoutePaths.studentSelectReservationTimeSlot}
            component={StudentSelectReservationTimeSlot}
          />
          <StudentSelectTimeSlotRoute
            exact
            path={RoutePaths.studentSelectTimeSlot}
            component={StudentSelectTimeSlot}
          />
          <ExpertRevenueManagementRoute
            exact
            path={RoutePaths.expertRevenueManagement}
            component={ExpertRevenueManagement}
          />
          <ExpertViewEnrollmentsRoute
            exact
            path={RoutePaths.expertViewEnrollments}
            component={ExpertViewEnrollments}
          />
          <ExpertEnrollmentsLessonReportRoute
            exact
            path={RoutePaths.expertEnrollmentsLessonReport}
            component={ExpertEnrollmentsLessonReport}
          />
          <StudentPurchasePointsRoute
            exact
            path={RoutePaths.studentPurchasePoints}
            component={StudentPurchasePoints}
          />
          <PasswordResetRoute
            exact
            path={RoutePaths.passwordReset}
            component={PasswordReset}
          />
          <ServiceAgreementRoute
            exact
            path={RoutePaths.serviceAgreement}
            component={ServiceAgreement}
          />
          <UserGuideRoute
            exact
            path={RoutePaths.userGuide}
            component={UserGuide}
          />
          <StaticPageRoute
            exact
            path={RoutePaths.paymentPolicy}
            component={PaymentPolicy}
          />
          <StaticPageRoute
            exact
            path={RoutePaths.InstructorPolicy}
            component={InstructorPolicy}
          />
          <StaticPageRoute
            exact
            path={RoutePaths.howToUseGuide}
            component={HowToUseGuide}
          />
          <StaticPageRoute
            exact
            path={RoutePaths.transactionLaw}
            component={TransactionLaw}
          />
          <StaticPageRoute
            exact
            path={RoutePaths.termsOfService}
            component={TermsOfService}
          />
          <StaticPageRoute
            exact
            path={RoutePaths.privacyPolicy}
            component={privacyPolicy}
          />
          <StaticPageRoute
            exact
            path={RoutePaths.cancellationPolicy}
            component={CancellationPolicy}
          />
          <StaticPageRoute
            exact
            path={RoutePaths.expertRegisterInfo}
            component={ExpertRegisterInfo}
          />
          <TeacherDetailsRoute
            exact
            path={RoutePaths.teacherDetails}
            component={TeacherDetails}
          />
          <StaticPageRoute
            exact
            path={RoutePaths.expertRegisterConfirm}
            component={ExpertRegisterationConfirm}
          />
          <ContactFormRoute
            exact
            path={RoutePaths.contactForm}
            component={ContactForm}
          />
          <ContactFormSuccessRoute
            exact
            path={RoutePaths.contactFormSuccess}
            component={ContactFormSuccess}
          />
          <PageNotFoundRoute path={"*"} component={NotFound} />
        </Switch>
      </Suspense>
    </BrowserRouter>
  );
};

export default Router;
